import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { Supplier } from "../models/Supplier";
import { SUPPLIER_INITIAL_VALUES } from "../models/Supplier";
import { useSuppliers } from "../hooks/useSuppliers";
import {
	useDeleteSupplier,
	useUpsertSupplier,
} from "../hooks/useSupplierMutation";
import { SuppliersTable } from "./SuppliersTable";
import { SupplierDetailViewer } from "./SupplierDetailViewer";
import { SuppliersToolbar } from "./SuppliersToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import { urlParam, useUrlState, useUrlText } from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	supplierId: urlParam.int(),
};

export const SuppliersManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, supplierId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [supplierToDelete, setSupplierToDelete] = useState<Supplier | null>(
		null,
	);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "suppliers-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { suppliers, isLoading, error: queryError } = useSuppliers();

	const filteredSuppliers = useMemo(() => {
		const base = onlyActives
			? suppliers.filter((sup) => sup.Active)
			: suppliers;
		if (!searchText) return base;
		const s = searchText.toLowerCase();
		return base.filter(
			(sup) =>
				sup.Name?.toLowerCase().includes(s) ||
				sup.ShortName?.toLowerCase().includes(s) ||
				sup.Number?.toLowerCase().includes(s),
		);
	}, [suppliers, searchText, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<Supplier>({
		id: supplierId,
		setId: (value) => setFilters({ supplierId: value }),
		dropId: () => dropValues("supplierId"),
		rows: suppliers,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedSupplier = selection.current;

	const { upsertSupplier, isUpserting } = useUpsertSupplier({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew
					? "Supplier created successfully"
					: "Supplier updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteSupplier, isDeleting } = useDeleteSupplier({
		onSuccess: () => {
			setNotification("Supplier deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setSupplierToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(supplier: Supplier | null) => {
			if (supplier) selection.select(supplier);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((supplier: Supplier) => {
		setSupplierToDelete(supplier);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (supplierToDelete?.Id) {
			deleteSupplier(supplierToDelete.Id);
		}
	}, [supplierToDelete, deleteSupplier]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setSupplierToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...SUPPLIER_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(supplier: Supplier) => {
			upsertSupplier(supplier);
		},
		[upsertSupplier],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Suppliers
			</Typography>

			<Paper elevation={3}>
				<SuppliersToolbar
					onReset={() => resetValues(["search", "onlyActives"])}
					canReset={!isDefault("search", "onlyActives")}
					search={searchText}
					onSearchChange={setSearchText}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="suppliers-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<SuppliersTable
									suppliers={filteredSuppliers}
									selectedSupplier={
										selectedSupplier?.Id ? selectedSupplier : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<SupplierDetailViewer
								supplier={selectedSupplier}
								existingSuppliers={suppliers}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE SUPPLIER"
				itemName={supplierToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
